'use client';

import { useCommendaOSRead } from '@commenda/api/commenda-os-dsl';
import { useApiScope } from '@commenda/api/scope';
import { LoadingIndicator, SingleFileUploader } from '@commenda/kakashi';
import type { DataSourceConfig } from '@commenda/types';
import type { FileBasic } from '@commenda/types/src/file/types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DSLContextMarker } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import { STANDARD_CONTEXT_KEYS } from '../../types';
import { DocumentEffectivePeriodHelper } from '../../utils/document-effective-period-helper';
import { buildShareScopedFileUpload } from '../../utils/share-scoped-upload';
import { isRuleActive } from '../../utils/validation';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

/**
 * Shape of a document node returned by the DSL read endpoint.
 * Matches IDocumentDSLNode from the backend adapter.
 */
interface DocumentDSLNode {
  id: number | null;
  title: string;
  documentType: string | null;
  documentSubType: string;
  fileName: string | null;
  fileUrl: string | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  isUploaded: boolean;
  isTimeSensitive: boolean;
  isUnique: boolean;
}

interface DocumentComponentProps {
  fieldId: string;
  label?: string;
  guidance?: { text?: string };
  dataSource?: DataSourceConfig;
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  props?: {
    /** The document sub-type (e.g., 'EIN', 'Bylaws', 'CertificateOfIncorporation') */
    documentSubType?: string | null;
    /** Operation mode: 'read' for display only, 'read-write' for upload + display */
    mode?: 'read' | 'read-write';
    /** Effective period start (YYYY-MM-DD) of a time-sensitive document */
    effectiveStartDate?: string | null;
    /** Effective period end (YYYY-MM-DD) of a time-sensitive document */
    effectiveEndDate?: string | null;
    disabled?: boolean;
  };
}

/**
 * Document Component
 *
 * A DSL-aware component for uploading and displaying corporation documents
 * (COI, Articles of Association, Bylaws, EIN Letter, etc.).
 *
 * In read-write mode, it renders a file uploader backed by Commenda OS DSL.
 * In read mode, it renders the current file with a view link.
 *
 * The displayed label prefers the author's configured label; when the author
 * leaves the default, it falls back to the DSL document rule's documentName.
 *
 * Configuration:
 * - documentSubType: The LegacyDocumentType key (e.g., 'EIN', 'Bylaws')
 * - mode: 'read' for display only, 'read-write' for editable
 * - effectiveStartDate / effectiveEndDate: optional period of a time-sensitive
 *   document; the file is read and saved for exactly that period
 * - contextBindings: Used to get corporationId from context or a previous field
 *
 * Usage in form DSL JSON:
 * {
 *   "component": "Document",
 *   "fieldId": "einDocument",
 *   "props": { "documentSubType": "EIN", "mode": "read-write" },
 *   "dataSource": {
 *     "type": "commenda-os",
 *     "commendaOS": {
 *       "entity": "corporation",
 *       "contextBindings": [{ "contextKey": "corporationId", "sourceFieldId": "__form_context__" }]
 *     }
 *   }
 * }
 */
export const DocumentComponent = ({
  fieldId,
  label: labelOverride,
  guidance,
  dataSource,
  validation,
  props = {},
}: DocumentComponentProps) => {
  const { shareToken } = useApiScope();
  const shareScopedUpload = useMemo(
    () => buildShareScopedFileUpload(shareToken),
    [shareToken]
  );
  const { formData, updateField, isFieldVisible, isFieldDisabled, dslContext } =
    useContext(FormContext);
  const {
    documentSubType,
    mode = 'read-write',
    effectiveStartDate = null,
    effectiveEndDate = null,
    disabled: propsDisabled = false,
  } = props;
  const effectivePeriod = useMemo(
    () =>
      DocumentEffectivePeriodHelper.getConfiguredPeriod({
        effectiveStartDate,
        effectiveEndDate,
      }),
    [effectiveStartDate, effectiveEndDate]
  );

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const contextBindings = dataSource?.commendaOS?.contextBindings;
  const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;
  const VALID_CONTEXT_KEYS: readonly string[] = STANDARD_CONTEXT_KEYS;

  // Build effective context by merging global context with bound field values
  const effectiveContext = useMemo(() => {
    const ctx = { ...dslContext };
    if (contextBindings) {
      for (const binding of contextBindings) {
        if (binding.sourceFieldId === FORM_CONTEXT_MARKER) continue;
        if (!VALID_CONTEXT_KEYS.includes(binding.contextKey)) continue;
        const fieldValue = formData[binding.sourceFieldId];
        if (fieldValue !== undefined) {
          ctx[binding.contextKey] =
            typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
              ? Number(fieldValue)
              : (fieldValue as string | number);
        }
      }
    }
    const filteredCtx: typeof ctx = {};
    for (const key of Object.keys(ctx)) {
      if (VALID_CONTEXT_KEYS.includes(key)) filteredCtx[key] = ctx[key];
    }
    return filteredCtx;
  }, [dslContext, formData, contextBindings]);

  const hasMissingBoundContext = useMemo(() => {
    if (!contextBindings?.length) return false;
    const validBindings = contextBindings.filter((b) =>
      VALID_CONTEXT_KEYS.includes(b.contextKey)
    );
    if (!validBindings.length) return false;
    return validBindings.some((binding) => {
      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        return dslContext?.[binding.contextKey] == null;
      }
      const val = formData[binding.sourceFieldId];
      return val == null || val === '';
    });
  }, [contextBindings, formData, dslContext]);

  const readPath = useMemo(() => {
    if (!documentSubType || !effectiveContext?.corporationId) return null;
    const filters = DocumentEffectivePeriodHelper.getDocumentFilters(
      documentSubType,
      effectivePeriod
    );
    return `corporation.${DocumentEffectivePeriodHelper.buildDocumentsReadPath(filters)}`;
  }, [documentSubType, effectivePeriod, effectiveContext?.corporationId]);

  const { data: document, isLoading } = useCommendaOSRead<DocumentDSLNode>(
    readPath,
    effectiveContext as { companyId: number } | undefined,
    { skip: !readPath || hasMissingBoundContext }
  );

  // Local file state for the SingleFileUploader — initialized from formData (persisted
  // across step navigation) or from the DSL read result when the component first mounts.
  const [file, setFile] = useState<FileBasic | null>(() => {
    const saved = formData[fieldId] as {
      fileUrl?: string;
      fileName?: string;
      fileId?: number;
    } | null;
    if (saved?.fileUrl && saved?.fileName) {
      return {
        fileUrl: saved.fileUrl,
        fileName: saved.fileName,
        documentType: documentSubType as FileBasic['documentType'],
        ...(saved.fileId != null ? { id: saved.fileId } : {}),
      };
    }
    return null;
  });

  // Guard so we only pre-populate formData once on first load, avoiding infinite re-renders
  const hasPrePopulated = useRef(false);

  useEffect(() => {
    if (document?.isUploaded && document.fileUrl && document.fileName) {
      // SingleFileUploader needs the file id to resolve a signed url for preview/download.
      setFile({
        fileName: document.fileName,
        fileUrl: document.fileUrl,
        documentType: document.documentSubType as FileBasic['documentType'],
        ...(document.id != null ? { id: document.id } : {}),
      });
      if (!hasPrePopulated.current) {
        hasPrePopulated.current = true;
        updateField(fieldId, {
          fileUrl: document.fileUrl,
          fileName: document.fileName,
          documentType: documentSubType ?? document.documentSubType,
          ...effectivePeriod,
          ...(document.id != null ? { fileId: document.id } : {}),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    document?.id,
    document?.isUploaded,
    document?.fileUrl,
    document?.fileName,
    document?.documentSubType,
  ]);

  const handleFileChange = (uploadedFile: FileBasic) => {
    if (uploadedFile?.fileUrl && uploadedFile?.fileName) {
      updateField(fieldId, {
        fileUrl: uploadedFile.fileUrl,
        fileName: uploadedFile.fileName,
        documentType: documentSubType ?? uploadedFile.documentType,
        ...effectivePeriod,
        ...(uploadedFile.id != null ? { fileId: uploadedFile.id } : {}),
      });
    }
  };

  const hasCustomLabel = labelOverride && labelOverride !== 'Document';
  const resolvedDSLTitle =
    document?.title && document.title !== documentSubType
      ? document.title
      : null;
  // The author's explicit label always wins. Only fall back to the OS document
  // name (resolvedDSLTitle) when the author left the field's default label.
  const title = hasCustomLabel
    ? labelOverride
    : resolvedDSLTitle || labelOverride || 'Document';
  const isReadOnly = mode === 'read';
  const isDisabled =
    propsDisabled ||
    isFieldDisabled(fieldId) ||
    isReadOnly ||
    hasMissingBoundContext;

  // ─── Loading state ────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={title}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  // ─── Configuration missing ────────────────────────────────────────────────────
  if (!documentSubType) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <div className="p-4 border border-dashed rounded-md bg-bg-secondary">
          <p className="text-sm text-text-tertiary text-center">
            Configure document type in the property editor
          </p>
        </div>
      </div>
    );
  }

  // ─── Missing corporation context ──────────────────────────────────────────────
  if (!effectiveContext?.corporationId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={title}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Corporation context required to load document.
          </p>
        </div>
      </div>
    );
  }

  // ─── Read-only mode ───────────────────────────────────────────────────────────
  if (isReadOnly) {
    const displayFile =
      (formData[fieldId] as
        | { fileUrl: string; fileName: string }
        | undefined) ||
      (document?.isUploaded && document.fileUrl
        ? { fileUrl: document.fileUrl, fileName: document.fileName }
        : null);

    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={title}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
        {displayFile?.fileUrl ? (
          <div className="flex items-center gap-2 p-3 border rounded-md bg-bg-secondary">
            <span className="text-sm text-text-primary flex-1 truncate">
              {displayFile.fileName}
            </span>
            <a
              href={displayFile.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-text-link hover:underline shrink-0"
            >
              View
            </a>
          </div>
        ) : (
          <div className="p-3 border border-dashed rounded-md">
            <p className="text-sm text-text-tertiary">Not uploaded</p>
          </div>
        )}
      </div>
    );
  }

  // ─── Read-write mode ──────────────────────────────────────────────────────────
  return (
    <div className="mb-4" data-field-id={fieldId}>
      <LabelWithGuidanceTooltip
        label={title}
        guidance={guidance?.text}
        isRequired={isRequired}
      />
      <SingleFileUploader
        file={file as FileBasic}
        setFile={setFile}
        companyId={effectiveContext.companyId as number | undefined}
        documentType={documentSubType as FileBasic['documentType']}
        onFileUpload={shareScopedUpload}
        onFileChange={handleFileChange}
        disabled={isDisabled}
      />
    </div>
  );
};
